import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import adminApi from '../utils/adminRequest';
import './AdminUsers.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [pointsChange, setPointsChange] = useState(0);
  const [pointsReason, setPointsReason] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page,
        page_size: pageSize,
        ...(search && { search })
      });

      const response = await adminApi.get(`/api/v1/admin/users?${params}`);
      const data = response.data;

      if (data.success) {
        setUsers(data.data.users);
        setTotal(data.data.pagination.total);
        setError('');
      } else {
        setError(data.message || '获取用户列表失败');
      }
    } catch (err) {
      setError('网络错误，请检查连接');
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);
  
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadUsers();
  };

  const handleAdjustPoints = async () => {
    if (!selectedUser || pointsChange === 0) return;

    try {
      const response = await adminApi.put(`/api/v1/admin/users/${selectedUser.user_id}/points`, {
        points_change: pointsChange,
        reason: pointsReason
      });
      const data = response.data;

      if (data.success) {
        alert(`积分调整成功！\n用户: ${selectedUser.nickname}\n变化: ${pointsChange > 0 ? '+' : ''}${pointsChange}\n新积分: ${data.data.new_points}`);
        setShowPointsModal(false);
        setPointsChange(0);
        setPointsReason('');
        loadUsers();
      } else {
        alert(`调整失败: ${data.message}`);
      }
    } catch (err) {
      alert('调整失败，请稍后重试');
    }
  };

  const openPointsModal = (user) => {
    setSelectedUser(user);
    setShowPointsModal(true);
    setPointsChange(0);
    setPointsReason('');
  };

  const openUserDetailsModal = async (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    try {
      const response = await adminApi.get(`/api/v1/admin/users/${user.user_id}/details`);
      const data = response.data;
      
      if (data && data.success && data.data) {
        setUserDetails(data.data);
      } else {
        setUserDetails({
          user_info: {
            id: user.id,
            user_id: user.user_id,
            nickname: user.nickname,
            email: user.email || 'user@example.com',
            phone: user.phone || '138****8888',
            point: user.point,
            level: user.level,
            role: user.role,
            created_at: user.created_at,
            updated_at: new Date().toISOString()
          },
          recharge_records: [],
          points_history: [],
          statistics: {
            total_recharge: 0,
            total_points_earned: 0,
            total_points_spent: 0,
            current_points: user.point
          },
          usage_statistics: {
            this_month: {
              job_search: 0,
              job_assistant_chat: 0,
              resume_diagnosis: 0,
              interview_prep: 0,
              mock_interview: 0,
              meeting_assistant: 0,
              month: new Date().toISOString().slice(0, 7)
            },
            total: {
              job_search: 0,
              job_assistant_chat: 0,
              resume_diagnosis: 0,
              interview_prep: 0,
              mock_interview: 0,
              meeting_assistant: 0
            }
          }
        });
      }
    } catch (e) {
      setUserDetails({
        user_info: {
          id: user.id,
          user_id: user.user_id,
          nickname: user.nickname,
          email: user.email || 'user@example.com',
          phone: user.phone || '138****8888',
          point: user.point,
          level: user.level,
          role: user.role,
          created_at: user.created_at,
          updated_at: new Date().toISOString()
        },
        recharge_records: [],
        points_history: [],
        statistics: {
          total_recharge: 0,
          total_points_earned: 0,
          total_points_spent: 0,
          current_points: user.point
        },
        usage_statistics: {
          this_month: {
            job_search: 0,
            job_assistant_chat: 0,
            resume_diagnosis: 0,
            interview_prep: 0,
            mock_interview: 0,
            meeting_assistant: 0,
            month: new Date().toISOString().slice(0, 7)
          },
          total: {
            job_search: 0,
            job_assistant_chat: 0,
            resume_diagnosis: 0,
            interview_prep: 0,
            mock_interview: 0,
            meeting_assistant: 0
          }
        }
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const UserRow = ({ user }) => (
    <tr className="user-row">
      <td>{user.id}</td>
      <td>
        <div className="user-info">
          <div className="user-avatar">{user.nickname.charAt(0)}</div>
          <div className="user-details">
            <div className="user-name">{user.nickname}</div>
            <div className="user-id">{user.user_id}</div>
          </div>
        </div>
      </td>
      <td>{user.email || '-'}</td>
      <td>{user.phone || '-'}</td>
      <td>
        <span className="badge badge-points">{user.point}</span>
      </td>
      <td>
        <span className={`badge badge-level level-${user.level.toLowerCase()}`}>
          {user.level}
        </span>
      </td>
      <td>
        <span className={`badge badge-role role-${user.role}`}>
          {getRoleLabel(user.role)}
        </span>
      </td>
      <td>{new Date(user.created_at).toLocaleDateString()}</td>
      <td>
        <div className="actions">
          <button 
            className="btn btn-sm btn-primary"
            onClick={() => openPointsModal(user)}
          >
            调整积分
          </button>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => openUserDetailsModal(user)}
          >
            详情
          </button>
        </div>
      </td>
    </tr>
  );

  const getRoleLabel = (role) => {
    const roleMap = {
      'user': '用户',
      'support_admin': '客服',
      'operation_admin': '运营'
    };
    return roleMap[role] || role;
  };

  const totalPages = Math.ceil(total / pageSize);

  const UsageDonut = ({ stats, title }) => {
    const items = [
      { name: '职位搜索', value: stats?.job_search || 0, color: '#6B8AFF' },
      { name: '简历诊断', value: stats?.resume_diagnosis || 0, color: '#9B59B6' },
      { name: '面试押题', value: stats?.interview_prep || 0, color: '#FF4D79' },
      { name: '模拟面试', value: stats?.mock_interview || 0, color: '#FF8C2F' },
      { name: '会议助手', value: stats?.meeting_assistant || 0, color: '#00B5D8' },
    ];
    const totalVal = items.reduce((s, i) => s + i.value, 0);
    if (!totalVal) {
      return (
        <div className="no-data-chart">
          <p>暂无数据</p>
          <div className="chart-title">{title}</div>
        </div>
      );
    }
    return (
      <div style={{ width: '100%', height: 160, paddingTop: 6, paddingBottom: 6 }}>
        <ResponsiveContainer>
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={items}
              dataKey="value"
              nameKey="name"
              innerRadius={38}
              outerRadius={54}
              paddingAngle={2}
              cx="50%"
              cy="42%"
              legendType="circle"
              cornerRadius={4}
            >
              {items.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [`${value}次`, name]} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 500, color: '#4a5568', marginTop: 8 }}>{title}</div>
      </div>
    );
  };

  return (
    <div className="admin-users">
      <div className="users-header">
        <h2>用户管理</h2>
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="搜索用户（昵称/邮箱/手机号）"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">搜索</button>
        </form>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={loadUsers}>重试</button>
        </div>
      )}

      <div className="users-stats">
        <div className="stat-item">
          <span className="stat-label">总用户数</span>
          <span className="stat-value">{total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">当前页</span>
          <span className="stat-value">{page} / {totalPages}</span>
        </div>
      </div>

      <div className="users-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>加载中...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <p>暂无用户数据</p>
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>用户信息</th>
                <th>邮箱</th>
                <th>手机号</th>
                <th>积分</th>
                <th>等级</th>
                <th>角色</th>
                <th>注册时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => <UserRow key={user.id} user={user} />)}
            </tbody>
          </table>
        )}
      </div>

      {!loading && users.length > 0 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </button>
          <span className="page-info">
            第 {page} 页 / 共 {totalPages} 页
          </span>
          <button
            className="btn btn-secondary"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </button>
        </div>
      )}

      {/* 积分调整弹窗 */}
      {showPointsModal && (
        <div className="modal-overlay" onClick={() => setShowPointsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>调整用户积分</h3>
              <button className="close-btn" onClick={() => setShowPointsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-user-info">
                <div className="modal-user-avatar">{selectedUser?.nickname.charAt(0)}</div>
                <div>
                  <div className="modal-user-name">{selectedUser?.nickname}</div>
                  <div className="modal-user-current">当前积分: {selectedUser?.point}</div>
                </div>
              </div>

              <div className="form-group">
                <label>积分变化</label>
                <input
                  type="number"
                  value={pointsChange}
                  onChange={(e) => setPointsChange(parseInt(e.target.value) || 0)}
                  placeholder="输入正数增加，负数减少"
                  className="form-control"
                />
                <small className="form-hint">
                  调整后积分: {Math.max(0, (selectedUser?.point || 0) + pointsChange)}
                </small>
              </div>

              <div className="form-group">
                <label>调整原因</label>
                <textarea
                  value={pointsReason}
                  onChange={(e) => setPointsReason(e.target.value)}
                  placeholder="请输入调整原因（选填）"
                  className="form-control"
                  rows="3"
                />
              </div>

              <div className="quick-actions">
                <button onClick={() => setPointsChange(100)} className="quick-btn">+100</button>
                <button onClick={() => setPointsChange(500)} className="quick-btn">+500</button>
                <button onClick={() => setPointsChange(1000)} className="quick-btn">+1000</button>
                <button onClick={() => setPointsChange(-100)} className="quick-btn danger">-100</button>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPointsModal(false)}>
                取消
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleAdjustPoints}
                disabled={pointsChange === 0}
              >
                确认调整
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 用户详情弹窗 */}
      {showDetailsModal && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content user-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>用户详情</h3>
              <button className="close-btn" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {loadingDetails ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>加载中...</p>
                </div>
              ) : userDetails ? (
                <div className="user-details-content">
                  {/* 用户基本信息 */}
                  <div className="detail-section">
                    <h4>基本信息</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>用户ID:</label>
                        <span>{userDetails.user_info.user_id}</span>
                      </div>
                      <div className="detail-item">
                        <label>昵称:</label>
                        <span>{userDetails.user_info.nickname}</span>
                      </div>
                      <div className="detail-item">
                        <label>邮箱:</label>
                        <span>{userDetails.user_info.email || '-'}</span>
                      </div>
                      <div className="detail-item">
                        <label>手机号:</label>
                        <span>{userDetails.user_info.phone || '-'}</span>
                      </div>
                      <div className="detail-item">
                        <label>等级:</label>
                        <span>{userDetails.user_info.level}</span>
                      </div>
                      <div className="detail-item">
                        <label>角色:</label>
                        <span>{getRoleLabel(userDetails.user_info.role)}</span>
                      </div>
                      <div className="detail-item">
                        <label>注册时间:</label>
                        <span>{new Date(userDetails.user_info.created_at).toLocaleString()}</span>
                      </div>
                      <div className="detail-item">
                        <label>最后更新:</label>
                        <span>{new Date(userDetails.user_info.updated_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* 统计数据 */}
                  <div className="detail-section">
                    <h4>统计数据</h4>
                    <div className="stats-grid">
                      <div className="stat-box">
                        <div className="stat-label">累计充值</div>
                        <div className="stat-value">¥{userDetails.statistics.total_recharge.toFixed(2)}</div>
                      </div>
                      <div className="stat-box">
                        <div className="stat-label">累计获得积分</div>
                        <div className="stat-value">+{userDetails.statistics.total_points_earned}</div>
                      </div>
                      <div className="stat-box">
                        <div className="stat-label">累计消费积分</div>
                        <div className="stat-value">-{userDetails.statistics.total_points_spent}</div>
                      </div>
                      <div className="stat-box">
                        <div className="stat-label">当前积分</div>
                        <div className="stat-value">{userDetails.statistics.current_points}</div>
                      </div>
                    </div>
                  </div>

                  {/* 功能使用情况 */}
                  {userDetails.usage_statistics && (
                    <div className="detail-section">
                      <h4>功能使用情况</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4, marginBottom: 4 }}>
                        <UsageDonut
                          stats={userDetails.usage_statistics.this_month}
                          title={`本月 ${userDetails.usage_statistics.this_month?.month || ''}`}
                        />
                        <UsageDonut
                          stats={userDetails.usage_statistics.total}
                          title="累计"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* 功能费用明细 */}
                  {userDetails.feature_costs && (
                    <div className="detail-section">
                      <h4>功能费用明细</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                            本月费用（{userDetails.feature_costs.currency}）
                          </div>
                          {(userDetails.feature_costs.this_month && userDetails.feature_costs.this_month.length > 0) ? (
                            <table className="records-table">
                              <thead>
                                <tr>
                                  <th>功能</th>
                                  <th>费用</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userDetails.feature_costs.this_month.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.feature_name}</td>
                                    <td>¥{Number(item.cost || 0).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="no-data">暂无数据</p>
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                            累计费用（{userDetails.feature_costs.currency}）
                          </div>
                          {(userDetails.feature_costs.total && userDetails.feature_costs.total.length > 0) ? (
                            <table className="records-table">
                              <thead>
                                <tr>
                                  <th>功能</th>
                                  <th>费用</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userDetails.feature_costs.total.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.feature_name}</td>
                                    <td>¥{Number(item.cost || 0).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="no-data">暂无数据</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 充值记录 */}
                  <div className="detail-section">
                    <h4>充值记录</h4>
                    {userDetails.recharge_records.length > 0 ? (
                      <div className="records-list">
                        <table className="records-table">
                          <thead>
                            <tr>
                              <th>订单号</th>
                              <th>金额</th>
                              <th>状态</th>
                              <th>支付方式</th>
                              <th>创建时间</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userDetails.recharge_records.map(record => (
                              <tr key={record.id}>
                                <td>{record.order_no}</td>
                                <td>¥{record.amount.toFixed(2)}</td>
                                <td>
                                  <span className={`status-badge status-${record.status}`}>
                                    {record.status === 'paid' ? '已支付' : 
                                     record.status === 'pending' ? '待支付' :
                                     record.status === 'failed' ? '支付失败' : 
                                     record.status === 'refunded' ? '已退款' : record.status}
                                  </span>
                                </td>
                                <td>{record.payment_method === 'alipay' ? '支付宝' : 
                                     record.payment_method === 'wechat' ? '微信' : 
                                     record.payment_method === 'unionpay' ? '银联' : record.payment_method}</td>
                                <td>{new Date(record.created_at).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-data">暂无充值记录</p>
                    )}
                  </div>

                  {/* 积分变化记录 */}
                  <div className="detail-section">
                    <h4>积分变化记录</h4>
                    {userDetails.points_history.length > 0 ? (
                      <div className="records-list">
                        <table className="records-table">
                          <thead>
                            <tr>
                              <th>时间</th>
                              <th>变化</th>
                              <th>原因</th>
                              <th>备注</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userDetails.points_history.map(record => (
                              <tr key={record.id}>
                                <td>{new Date(record.created_at).toLocaleString()}</td>
                                <td>
                                  <span className={record.change > 0 ? 'points-earned' : 'points-spent'}>
                                    {record.change > 0 ? '+' : ''}{record.change}
                                  </span>
                                </td>
                                <td>{record.reason}</td>
                                <td>{record.meta || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-data">暂无积分变化记录</p>
                    )}
                  </div>
                </div>
              ) : (
                <p>加载失败</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
